import { api } from "./base_service";
import { apiEndpoints } from "@/src/config/api/endpoints";
import type { ApiError, Paginated, Result } from "./types";

/** The `/company/` object as the backend sends it (snake_case, bare object). */
export interface CompanyRaw {
  name: string;
  vat: string;
  address_line_1: string;
  address_line_2?: string | null;
  city: string;
  postal_code: string;
  country: string;
  state?: string | null;
}

/** Company profile at the camelCase boundary (consumed by the Company page). */
export interface Company {
  name: string;
  vatId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  state: string;
}

export function adaptCompany(raw: CompanyRaw): Company {
  return {
    name: raw.name ?? "",
    vatId: raw.vat ?? "",
    addressLine1: raw.address_line_1 ?? "",
    addressLine2: raw.address_line_2 ?? "",
    city: raw.city ?? "",
    postalCode: raw.postal_code ?? "",
    country: raw.country ?? "",
    state: raw.state ?? "",
  };
}

/**
 * Map a partial camelCase `Company` to the snake_case POST/PATCH payload,
 * including only the keys actually present (the page saves info + address as two
 * separate forms, each a partial update).
 */
function toCompanyPayload(input: Partial<Company>): Record<string, string> {
  const out: Record<string, string> = {};
  if (input.name !== undefined) out.name = input.name;
  if (input.vatId !== undefined) out.vat = input.vatId;
  if (input.addressLine1 !== undefined) out.address_line_1 = input.addressLine1;
  if (input.addressLine2 !== undefined) out.address_line_2 = input.addressLine2;
  if (input.city !== undefined) out.city = input.city;
  if (input.postalCode !== undefined) out.postal_code = input.postalCode;
  if (input.country !== undefined) out.country = input.country;
  if (input.state !== undefined) out.state = input.state;
  return out;
}

export async function getCompany(): Promise<Result<Company>> {
  try {
    const data = await api.get<CompanyRaw>(apiEndpoints.company().detail());
    return { ok: true, value: adaptCompany(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function updateCompany(input: Partial<Company>): Promise<Result<Company>> {
  try {
    const data = await api.patch<CompanyRaw>(
      apiEndpoints.company().update(),
      toCompanyPayload(input),
    );
    return { ok: true, value: adaptCompany(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** `PATCH /company/config/` — company-wide settings; only `enforce_sso` is wired
 * so far. Owner/admin only (`BackOfficeCompanyPermission`); the backend 400s if
 * the calling user has no connected SSO provider of their own yet. */
export async function updateCompanyConfig(input: {
  enforceSso: boolean;
}): Promise<Result<{ enforceSso: boolean }>> {
  try {
    const data = await api.patch<{ enforce_sso: boolean }>(
      apiEndpoints.company().config().update(),
      {
        enforce_sso: input.enforceSso,
      },
    );
    return { ok: true, value: { enforceSso: !!data.enforce_sso } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * Data retention tri-state (`users.constants.DATA_RETENTION_CHOICES`):
 * "disabled" (default — no history kept beyond the backend's short-lived
 * window), "requested" (a company admin asked sanctions.io to turn it on;
 * pending manual activation), "enabled" (history is retained and viewable).
 * Two ways to move off "disabled": `requestDataRetention` (→ "requested",
 * pending manual staff activation — the dashboard's quick-prompt path) or
 * `enableDataRetention` (→ "enabled" directly, only after the signer
 * completes the DPA sign flow — the signed contract is the authorization).
 */
export type DataRetentionStatus = "disabled" | "requested" | "enabled";

/**
 * How long retained data is kept (`users.constants.DATA_RETENTION_PERIOD_TO_DAYS`).
 * Written to the backend as this period NAME (`data_retention_days`), but read
 * back as a plain day count — the field is asymmetric, see DATA_RETENTION_DAYS_TO_PERIOD.
 * Settable only while the company is "requested" or "enabled" (backend 400s a
 * period change while "disabled").
 */
export type DataRetentionPeriod = "day" | "week" | "month" | "year" | "indefinite";

const DATA_RETENTION_FROM_WIRE: Record<string, DataRetentionStatus> = {
  Disabled: "disabled",
  Requested: "requested",
  Enabled: "enabled",
};

const DATA_RETENTION_PERIOD_TO_DAYS: Record<DataRetentionPeriod, number> = {
  day: 1,
  week: 7,
  month: 30,
  year: 365,
  indefinite: 9999,
};

/**
 * `dataRetentionDays` back to a period name — the closest preset by day count,
 * not just an exact match, since the backend field is a plain integer (e.g. a
 * value set before this UI existed, or a slightly-off count) that won't always
 * land exactly on 1/7/30/365/9999.
 */
function daysToPeriod(days: number): DataRetentionPeriod {
  let closest: DataRetentionPeriod = "day";
  let closestDiff = Infinity;
  for (const [period, presetDays] of Object.entries(DATA_RETENTION_PERIOD_TO_DAYS) as [
    DataRetentionPeriod,
    number,
  ][]) {
    const diff = Math.abs(presetDays - days);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = period;
    }
  }
  return closest;
}

export interface CompanyDataRetention {
  dataRetention: DataRetentionStatus;
  /** Raw day count as the backend stores it (e.g. 7). Null if never set. */
  dataRetentionDays: number | null;
  /** `dataRetentionDays` mapped to the closest of the five presets — the
   *  model always has a day count (default 1), so this is never null. */
  dataRetentionPeriod: DataRetentionPeriod;
}

function adaptDataRetention(data: {
  data_retention?: string;
  data_retention_days?: number;
}): CompanyDataRetention {
  const days = typeof data.data_retention_days === "number" ? data.data_retention_days : null;
  return {
    dataRetention: DATA_RETENTION_FROM_WIRE[data.data_retention ?? ""] ?? "disabled",
    dataRetentionDays: days,
    dataRetentionPeriod: daysToPeriod(days ?? DATA_RETENTION_PERIOD_TO_DAYS.day),
  };
}

export async function getCompanyConfig(): Promise<Result<CompanyDataRetention>> {
  try {
    const data = await api.get<{ data_retention?: string; data_retention_days?: number }>(
      apiEndpoints.company().config().detail(),
    );
    return { ok: true, value: adaptDataRetention(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * `PATCH /company/config/` with `data_retention: "Requested"` (+ optionally
 * `data_retention_days` in the same call) — a company admin opting in. Owner/
 * admin only; the backend notifies sanctions.io to manually flip the company
 * to "enabled" (email fires once, the first time a company moves off
 * "disabled"). Used by the dashboard's quick-prompt path, which doesn't
 * collect a signed DPA — see `enableDataRetention` for the signed path.
 */
export async function requestDataRetention(
  period?: DataRetentionPeriod,
): Promise<Result<CompanyDataRetention>> {
  const payload: Record<string, string> = { data_retention: "Requested" };
  if (period) payload.data_retention_days = period;
  try {
    const data = await api.patch<{ data_retention?: string; data_retention_days?: number }>(
      apiEndpoints.company().config().update(),
      payload,
    );
    return { ok: true, value: adaptDataRetention(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * `PATCH /company/config/` with `data_retention: "Enabled"` — skips the
 * "Requested" staff-review step. Only call this right after
 * `submitContractSignature` succeeds; the signed DPA is what authorizes
 * going straight to "enabled" instead of waiting on manual activation.
 * NOTE: this relies on the backend accepting "Enabled" from a non-staff
 * PATCH for a company with a matching signed contract on file — confirm
 * that's actually enforced server-side, since historically only sanctions.io
 * staff could set this value.
 */
export async function enableDataRetention(
  period?: DataRetentionPeriod,
): Promise<Result<CompanyDataRetention>> {
  const payload: Record<string, string> = { data_retention: "Enabled" };
  if (period) payload.data_retention_days = period;
  try {
    const data = await api.patch<{ data_retention?: string; data_retention_days?: number }>(
      apiEndpoints.company().config().update(),
      payload,
    );
    return { ok: true, value: adaptDataRetention(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function disableDataRetention(): Promise<Result<CompanyDataRetention>> {
  try {
    const data = await api.patch<{ data_retention?: string; data_retention_days?: number }>(
      apiEndpoints.company().config().update(),
      { data_retention: "Disabled" },
    );
    return { ok: true, value: adaptDataRetention(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * `PATCH /company/config/` with only `data_retention_days` — changes the
 * retention window without resubmitting `data_retention`. The backend 400s
 * this unless the company is already "requested" or "enabled".
 */
export async function updateDataRetentionPeriod(
  period: DataRetentionPeriod,
): Promise<Result<CompanyDataRetention>> {
  try {
    const data = await api.patch<{ data_retention?: string; data_retention_days?: number }>(
      apiEndpoints.company().config().update(),
      { data_retention_days: period },
    );
    return { ok: true, value: adaptDataRetention(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** Backend `ContractType` choice for the DPA (`users.constants.CONTRACT_TYPE_DATA_RETENTION`). */
const CONTRACT_TYPE_DPA = "data-retention";

export interface ActiveContract {
  id: string;
  version: string;
}

/**
 * `GET /contracts/?contract_type=data-retention` — the current official
 * `Contract` row to sign.
 */
export async function getActiveDpaContract(): Promise<Result<ActiveContract>> {
  try {
    const data = await api.get<{ contracts: { id: string; version: string }[] }>(
      apiEndpoints.contracts().list(),
      { params: { contract_type: CONTRACT_TYPE_DPA } },
    );
    const contract = data.contracts?.[0];
    if (!contract) {
      return {
        ok: false,
        error: {
          status: 404,
          code: "not_found",
          message: "No active data retention contract is configured.",
          requestId: "client",
        },
      };
    }
    return { ok: true, value: { id: contract.id, version: contract.version } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/**
 * `POST /company/contracts/` (`ContractSignatureListCreateViewSet`) — records
 * that the current user signed `contractId`. Company, user, timestamp, and IP
 * are all derived server-side from the authenticated request. The view only
 * accepts `multipart/form-data` (`parser_classes = [MultiPartParser]`) and
 * requires the signed PDF as `signed_document` — it uploads it to S3 and
 * rejects the request with a 415 if sent as JSON.
 */
export async function submitContractSignature(
  contractId: string,
  signedDocument: Blob,
): Promise<Result<DpaSignature>> {
  try {
    const formData = new FormData();
    formData.append("contract_id", contractId);
    formData.append("signed_document", signedDocument, "sanctions.io-dpa-signed.pdf");
    const data = await api.post<{ user: string; signed_at: string }>(
      apiEndpoints.company().contracts().create(),
      formData,
    );
    return { ok: true, value: { signerEmail: data.user, signedAt: data.signed_at } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export interface DpaSignature {
  signerEmail: string;
  signedAt: string;
}

/**
 * `GET /company/contracts/?contract_type=data-retention` — the company's
 * latest signed DPA (`ContractSignatureListCreateViewSet.list()`, already
 * scoped to the current company and to the newest signature per contract
 * type). Used to show who signed and when once retention is enabled.
 */
export async function getDpaSignature(): Promise<Result<DpaSignature>> {
  try {
    const data = await api.get<{
      contracts: { user: string; signed_at: string }[];
    }>(apiEndpoints.company().contracts().list(), {
      params: { contract_type: CONTRACT_TYPE_DPA },
    });
    const signature = data.contracts?.[0];
    if (!signature) {
      return {
        ok: false,
        error: {
          status: 404,
          code: "not_found",
          message: "No signed data retention agreement was found.",
          requestId: "client",
        },
      };
    }
    return { ok: true, value: { signerEmail: signature.user, signedAt: signature.signed_at } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

// ---------------------------------------------------------------------------
// Company users / team  (GET / POST / PATCH / DELETE  /user/)
// ---------------------------------------------------------------------------

/** A `/user/` entry as the backend sends it (snake_case). */
export interface UserAccountRaw {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  username?: string;
  /** Backend role token: owner|admin|integration|compliance|auditor. */
  account_type: string;
  enable_monitoring: boolean;
  /** False until the invitee accepts the invite and sets up their account. */
  is_active: boolean;
  date_joined?: string | null;
  /** Last sign-in (added to the v2.4 response 2026-06-18). Null = never signed in. */
  last_login?: string | null;
  sso_enabled?: boolean;
}

/** A company user at the camelCase boundary. */
export interface UserAccount {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  /** Backend role token (owner|admin|integration|compliance|auditor); UI maps it. */
  accountType: string;
  enableMonitoring: boolean;
  isActive: boolean;
  dateJoined: string;
  /** Last sign-in timestamp (v2.4). Empty string = never signed in. */
  lastLogin: string;
  ssoEnabled: boolean;
}

/** A page of company users plus the seat-limit the team UI surfaces. */
export interface UserListPage {
  users: UserAccount[];
  count: number;
  maxUsersAllowed?: number;
}

export function adaptUser(raw: UserAccountRaw): UserAccount {
  return {
    uuid: raw.uuid,
    email: raw.email ?? "",
    firstName: raw.first_name ?? "",
    lastName: raw.last_name ?? "",
    username: raw.username ?? "",
    accountType: raw.account_type ?? "",
    enableMonitoring: !!raw.enable_monitoring,
    isActive: !!raw.is_active,
    dateJoined: raw.date_joined ?? "",
    lastLogin: raw.last_login ?? "",
    ssoEnabled: !!raw.sso_enabled,
  };
}

/** The backend wants `enable_monitoring` as a "true"/"false" string (ref user_service). */
const monitoringFlag = (on: boolean): string => (on ? "true" : "false");

export async function getUser(uuid: string): Promise<Result<UserAccount>> {
  try {
    const data = await api.get<UserAccountRaw>(apiEndpoints.company().users().detail(uuid));
    return { ok: true, value: adaptUser(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function listUsers(page = 1, pageSize = 25): Promise<Result<UserListPage>> {
  try {
    const data = await api.get<
      Paginated<UserAccountRaw> & { extra_data?: { max_users_allowed?: number } }
    >(apiEndpoints.company().users().list(), { params: { page, page_size: pageSize } });
    return {
      ok: true,
      value: {
        users: (data.results ?? []).map(adaptUser),
        count: data.count ?? 0,
        maxUsersAllowed: data.extra_data?.max_users_allowed,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export interface InviteUserInput {
  email: string;
  /** Backend role token (owner|admin|integration|compliance|auditor). */
  accountType: string;
  enableMonitoring: boolean;
}

/** `POST /user/` — invite a new user to the company. */
export async function inviteUser(input: InviteUserInput): Promise<Result<UserAccount>> {
  try {
    const data = await api.post<UserAccountRaw>(apiEndpoints.company().users().create(), {
      email: input.email,
      account_type: input.accountType,
      enable_monitoring: monitoringFlag(input.enableMonitoring),
    });
    return { ok: true, value: adaptUser(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export interface UpdateUserInput {
  accountType?: string;
  enableMonitoring?: boolean;
}

export async function updateUser(
  uuid: string,
  input: UpdateUserInput,
): Promise<Result<UserAccount>> {
  const payload: Record<string, string> = {};
  if (input.accountType !== undefined) payload.account_type = input.accountType;
  if (input.enableMonitoring !== undefined)
    payload.enable_monitoring = monitoringFlag(input.enableMonitoring);
  try {
    const data = await api.patch<UserAccountRaw>(
      apiEndpoints.company().users().update(uuid),
      payload,
    );
    return { ok: true, value: adaptUser(data) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function deleteUser(uuid: string): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.company().users().delete(uuid));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
